'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organization: string;
  facilityType: string;
  equipmentCategory: string;
  equipmentDetails: string;
  quantity: string;
  timeline: string;
  budget: string;
  installationRequired: boolean;
  complianceDocumentation: boolean;
  additionalNotes: string;
}

interface FormErrors {
  [key: string]: string;
}

const QuoteRequestForm: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    facilityType: '',
    equipmentCategory: '',
    equipmentDetails: '',
    quantity: '',
    timeline: '',
    budget: '',
    installationRequired: false,
    complianceDocumentation: false,
    additionalNotes: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const facilityTypes = [
    'Hospital',
    'Surgical Center',
    'Clinic',
    'Imaging Center',
    'Medical Practice',
    'Research Facility',
    'Other'
  ];

  const equipmentCategories = [
    'C-Arm Systems',
    'Surgical Tables',
    'Imaging Equipment',
    'Sterilization Equipment',
    'Patient Monitoring',
    'Surgical Instruments',
    'Other'
  ];

  const timelineOptions = [
    'Immediate (1-2 weeks)',
    'Short-term (1 month)',
    'Medium-term (2-3 months)',
    'Long-term (3+ months)',
    'Flexible'
  ];

  const budgetRanges = [
    'Under $50,000',
    '$50,000 - $100,000',
    '$100,000 - $250,000',
    '$250,000 - $500,000',
    'Over $500,000',
    'Prefer not to disclose'
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.organization.trim()) newErrors.organization = 'Organization name is required';
      if (!formData.facilityType) newErrors.facilityType = 'Please select a facility type';
    }

    if (step === 2) {
      if (!formData.equipmentCategory) newErrors.equipmentCategory = 'Please select an equipment category';
      if (!formData.equipmentDetails.trim()) newErrors.equipmentDetails = 'Equipment details are required';
      if (!formData.quantity.trim()) newErrors.quantity = 'Quantity is required';
      if (!formData.timeline) newErrors.timeline = 'Please select a timeline';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      setCurrentStep(1);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organization: '',
        facilityType: '',
        equipmentCategory: '',
        equipmentDetails: '',
        quantity: '',
        timeline: '',
        budget: '',
        installationRequired: false,
        complianceDocumentation: false,
        additionalNotes: ''
      });
    }, 3000);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="bg-card border border-border rounded-lg p-8 shadow-brand">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-muted rounded w-full mb-8"></div>
              <div className="space-y-4">
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (submitSuccess) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="bg-card border border-border rounded-lg p-8 shadow-brand text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-6">
              <Icon name="CheckCircleIcon" size={32} className="text-success" />
            </div>
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Quote Request Submitted Successfully!
            </h3>
            <p className="text-text-secondary mb-6">
              Thank you for your interest in AIVA Medical Supply. Our team will review your request and contact you within 24 hours with a detailed quote and consultation options.
            </p>
            <div className="bg-surface border border-border rounded-lg p-4 inline-block">
              <p className="text-sm text-text-secondary">
                Reference Number: <span className="font-semibold text-primary">AIVA-{Date.now().toString().slice(-8)}</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <div className="bg-card border border-border rounded-lg p-8 shadow-brand">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-text-primary mb-2">
              Request a Quote
            </h2>
            <p className="text-text-secondary">
              Complete this form to receive a detailed quote for your medical equipment needs
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300 ${
                        currentStep >= step
                          ? 'bg-primary text-white' :'bg-muted text-text-secondary'
                      }`}
                    >
                      {currentStep > step ? (
                        <Icon name="CheckIcon" size={20} />
                      ) : (
                        step
                      )}
                    </div>
                    <span className={`ml-3 text-sm font-medium hidden sm:inline ${
                      currentStep >= step ? 'text-text-primary' : 'text-text-secondary'
                    }`}>
                      {step === 1 && 'Contact Info'}
                      {step === 2 && 'Equipment Details'}
                      {step === 3 && 'Additional Info'}
                    </span>
                  </div>
                  {step < 3 && (
                    <div className={`flex-1 h-1 mx-4 rounded transition-all duration-300 ${
                      currentStep > step ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-2">
                      First Name <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                        errors.firstName ? 'border-error' : 'border-input'
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-error flex items-center">
                        <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-2">
                      Last Name <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                        errors.lastName ? 'border-error' : 'border-input'
                      }`}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-error flex items-center">
                        <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                    Email Address <span className="text-error">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                      errors.email ? 'border-error' : 'border-input'
                    }`}
                    placeholder="john.doe@hospital.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                    Phone Number <span className="text-error">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                      errors.phone ? 'border-error' : 'border-input'
                    }`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-text-primary mb-2">
                    Organization Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                      errors.organization ? 'border-error' : 'border-input'
                    }`}
                    placeholder="Memorial Hospital"
                  />
                  {errors.organization && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.organization}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="facilityType" className="block text-sm font-medium text-text-primary mb-2">
                    Facility Type <span className="text-error">*</span>
                  </label>
                  <select
                    id="facilityType"
                    name="facilityType"
                    value={formData.facilityType}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                      errors.facilityType ? 'border-error' : 'border-input'
                    }`}
                  >
                    <option value="">Select facility type</option>
                    {facilityTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.facilityType && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.facilityType}
                    </p>
                  )}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="equipmentCategory" className="block text-sm font-medium text-text-primary mb-2">
                    Equipment Category <span className="text-error">*</span>
                  </label>
                  <select
                    id="equipmentCategory"
                    name="equipmentCategory"
                    value={formData.equipmentCategory}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                      errors.equipmentCategory ? 'border-error' : 'border-input'
                    }`}
                  >
                    <option value="">Select equipment category</option>
                    {equipmentCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  {errors.equipmentCategory && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.equipmentCategory}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="equipmentDetails" className="block text-sm font-medium text-text-primary mb-2">
                    Equipment Details <span className="text-error">*</span>
                  </label>
                  <textarea
                    id="equipmentDetails"
                    name="equipmentDetails"
                    value={formData.equipmentDetails}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 resize-none ${
                      errors.equipmentDetails ? 'border-error' : 'border-input'
                    }`}
                    placeholder="Please provide specific model numbers, technical specifications, or detailed requirements..."
                  />
                  {errors.equipmentDetails && (
                    <p className="mt-1 text-sm text-error flex items-center">
                      <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                      {errors.equipmentDetails}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-text-primary mb-2">
                      Quantity <span className="text-error">*</span>
                    </label>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                        errors.quantity ? 'border-error' : 'border-input'
                      }`}
                      placeholder="1"
                    />
                    {errors.quantity && (
                      <p className="mt-1 text-sm text-error flex items-center">
                        <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                        {errors.quantity}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-text-primary mb-2">
                      Timeline <span className="text-error">*</span>
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                        errors.timeline ? 'border-error' : 'border-input'
                      }`}
                    >
                      <option value="">Select timeline</option>
                      {timelineOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {errors.timeline && (
                      <p className="mt-1 text-sm text-error flex items-center">
                        <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                        {errors.timeline}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-text-primary mb-2">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                  >
                    <option value="">Select budget range (optional)</option>
                    {budgetRanges.map((range) => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="installationRequired"
                      name="installationRequired"
                      checked={formData.installationRequired}
                      onChange={handleInputChange}
                      className="mt-1 w-5 h-5 text-primary border-input rounded focus:ring-2 focus:ring-primary"
                    />
                    <label htmlFor="installationRequired" className="ml-3 text-sm text-text-primary">
                      <span className="font-medium">Installation services required</span>
                      <p className="text-text-secondary mt-1">
                        Include professional installation, setup, and initial calibration
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="complianceDocumentation"
                      name="complianceDocumentation"
                      checked={formData.complianceDocumentation}
                      onChange={handleInputChange}
                      className="mt-1 w-5 h-5 text-primary border-input rounded focus:ring-2 focus:ring-primary"
                    />
                    <label htmlFor="complianceDocumentation" className="ml-3 text-sm text-text-primary">
                      <span className="font-medium">Request compliance documentation</span>
                      <p className="text-text-secondary mt-1">
                        Include FDA, CE, ISO certifications and regulatory compliance documents
                      </p>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="additionalNotes" className="block text-sm font-medium text-text-primary mb-2">
                    Additional Notes or Requirements
                  </label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 resize-none"
                    placeholder="Please share any additional information about your facility requirements, special considerations, or questions you may have..."
                  />
                </div>

                <div className="bg-surface border border-border rounded-lg p-6">
                  <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                    <Icon name="InformationCircleIcon" size={20} className="mr-2 text-primary" />
                    What Happens Next?
                  </h4>
                  <ul className="space-y-2 text-sm text-text-secondary">
                    <li className="flex items-start">
                      <Icon name="CheckCircleIcon" size={16} className="mr-2 mt-0.5 text-success flex-shrink-0" />
                      <span>Our team will review your requirements within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="CheckCircleIcon" size={16} className="mr-2 mt-0.5 text-success flex-shrink-0" />
                      <span>You'll receive a detailed quote with pricing, specifications, and delivery timeline</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="CheckCircleIcon" size={16} className="mr-2 mt-0.5 text-success flex-shrink-0" />
                      <span>A dedicated specialist will contact you to discuss your needs and answer questions</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="CheckCircleIcon" size={16} className="mr-2 mt-0.5 text-success flex-shrink-0" />
                      <span>We'll schedule a consultation to finalize equipment selection and implementation plan</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="px-6 py-3 text-sm font-medium text-text-primary bg-muted hover:bg-muted/80 rounded-lg transition-all duration-250 flex items-center"
                >
                  <Icon name="ArrowLeftIcon" size={16} className="mr-2" />
                  Previous
                </button>
              ) : (
                <div></div>
              )}

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-3 text-sm font-medium text-white bg-primary hover:bg-secondary rounded-lg transition-all duration-250 flex items-center ml-auto"
                >
                  Next
                  <Icon name="ArrowRightIcon" size={16} className="ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 text-sm font-medium text-white bg-brand-cta hover:bg-red-700 rounded-lg transition-all duration-250 flex items-center ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={16} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Quote Request
                      <Icon name="PaperAirplaneIcon" size={16} className="ml-2" />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteRequestForm;